import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api/client';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

export function SecurityTab() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      toast.error('New password must be at least 12 characters');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/auth/change-password/', {
        old_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });

      // Update token if a new one was returned
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const error = err as Record<string, unknown>;
      if (error['response']) {
        const resp = error['response'] as Record<string, unknown>;
        const data = resp['data'] as Record<string, unknown> | undefined;
        const details = data?.['details'] as Record<string, unknown> | undefined;
        const fields = details?.['fields'] as Record<string, string[]> | undefined;

        if (fields?.['old_password']) {
          toast.error(fields['old_password'][0] || 'Current password is incorrect');
        } else if (fields?.['new_password']) {
          toast.error(fields['new_password'][0] || 'Invalid new password');
        } else if (fields?.['new_password_confirm']) {
          toast.error(fields['new_password_confirm'][0] || 'Passwords do not match');
        } else if (data?.['message']) {
          toast.error(String(data['message']));
        } else {
          toast.error('Failed to update password');
        }
      } else {
        toast.error('Failed to update password');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>
            Update your password to keep your account secure.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="space-y-4"
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              handleChangePassword();
            }}
          >
            <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">Password requirements:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Minimum 12 characters</li>
                <li>Cannot be too similar to your personal information</li>
                <li>Cannot be a commonly used password</li>
                <li>Cannot be entirely numeric</li>
              </ul>
            </div>
            {/* Hidden username field so the browser's password manager has an explicit
                target and doesn't scavenge the nearest text input (e.g. the global
                search bar) as the username. */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={user?.email || ''}
              readOnly
              hidden
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
